import { useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
    isValidEmailAddress,
    notificationTypeCategoryLabel,
} from "./notificationTypeConsts";
import {
    TEMPLATE_VARIABLES,
    renderNotificationTemplate,
    renderTemplateText,
    validateTemplateBody,
    validateTemplateSubject,
    validateProvenanceNote,
    DEFAULT_PAYMENT_PROVENANCE_NOTE,
    PREVIEWABLE_TEMPLATE_CODES,
} from "../../helpers/notifications/emailTemplates";

const emptyDraft = {
    status: "active",
    in_app_enabled: true,
    slack_dm: false,
    slack_channel_ids: [],
    email_recipients: [],
    deliver_in_po_thread: true,
    cc: [],
    subject: "",
    body_html: "",
    provenance_note: "",
};

const parseConfig = (config) => {
    if (config && typeof config === "object" && !Array.isArray(config)) return config;
    if (typeof config !== "string") return {};
    try {
        const parsed = JSON.parse(config);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (_error) {
        return {};
    }
};

const isValidFrom = (value) => {
    const candidate = String(value || "").trim();
    if (!candidate) return true;
    if (isValidEmailAddress(candidate)) return true;
    const displayNameMatch = candidate.match(/^.+\s<([^<>]+)>$/);
    return !!displayNameMatch && isValidEmailAddress(displayNameMatch[1]);
};

const parseEmailList = (value) => {
    const values = Array.isArray(value)
        ? value
        : typeof value === "string"
            ? value.split(/[,;]/)
            : [];
    return values
        .map((address) => String(address || "").trim())
        .filter((address, index, addresses) => (
            isValidEmailAddress(address)
            && addresses.findIndex((item) => item.toLowerCase() === address.toLowerCase()) === index
        ));
};

const statusHelpText = (type) => {
    if (type.delivery_kind !== "outbound") {
        return "Paused stops this notification for everyone, on every channel.";
    }
    if (["SUPPLIER_SHIP_REMINDER", "SUPPLIER_SHIP_REMINDER_DUE"].includes(type.code)) {
        return "Pausing stops the automated reminders, while a reminder sent by hand from a PO still goes out.";
    }
    return "Pausing stops this supplier email from being sent.";
};

const draftFromType = (type, preference = {}) => {
    if (!type) return { ...emptyDraft };
    const draft = {
        status: type.status === "paused" ? "paused" : "active",
        in_app_enabled: type.in_app_enabled === true,
        slack_dm: preference.slack_dm === true,
        slack_channel_ids: Array.isArray(type.slack_channel_ids)
            ? [...type.slack_channel_ids]
            : (type.slack_channel_id ? [type.slack_channel_id] : []),
        email_recipients: Array.isArray(type.email_recipients)
            ? [...type.email_recipients]
            : [],
    };
    const config = parseConfig(type.config);
    if (type.delivery_kind !== "outbound") {
        const hasThresholdUnits = Object.prototype.hasOwnProperty.call(config, "threshold_units");
        if (hasThresholdUnits) {
            draft.has_threshold_units = true;
            draft.threshold_units = Number.isInteger(config.threshold_units)
                && config.threshold_units >= 1
                && config.threshold_units <= 100000
                ? String(config.threshold_units)
                : "";
        }
        return draft;
    }

    const hasOffsetDays = Object.prototype.hasOwnProperty.call(config, "offset_days");
    const offsetDays = hasOffsetDays && Number.isInteger(config.offset_days)
        && config.offset_days >= 0
        && config.offset_days <= 60
        ? String(config.offset_days)
        : "";
    const from = isValidFrom(config.from) ? String(config.from || "") : "";
    const bcc = parseEmailList(config.bcc);
    const cc = parseEmailList(config.cc);
    return {
        ...draft,
        has_offset_days: hasOffsetDays,
        offset_days: offsetDays,
        from,
        bcc,
        cc,
        subject: typeof config.subject === "string" ? config.subject : "",
        body_html: typeof config.body_html === "string" ? config.body_html : "",
        provenance_note: typeof config.provenance_note === "string"
            ? config.provenance_note
            : "",
        deliver_in_po_thread: config.deliver_in_po_thread !== false,
    };
};

const fieldLabel = "mb-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-400";
const textInput = "h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-gray-500 focus:ring-1 focus:ring-gray-900";

const NotificationTypeDrawer = ({
    type,
    preference,
    saving,
    sendingPreview,
    onClose,
    onSave,
    onSendPreview,
}) => {
    const slackDmPreference = preference?.slack_dm;
    const [draft, setDraft] = useState(draftFromType(type, { slack_dm: slackDmPreference }));
    const [recipientInput, setRecipientInput] = useState("");
    const [recipientError, setRecipientError] = useState("");
    const [bccInput, setBccInput] = useState("");
    const [bccError, setBccError] = useState("");
    const [ccInput, setCcInput] = useState("");
    const [ccError, setCcError] = useState("");
    const [offsetError, setOffsetError] = useState("");
    const [thresholdError, setThresholdError] = useState("");
    const [fromError, setFromError] = useState("");
    const [channelInput, setChannelInput] = useState("");
    const [channelError, setChannelError] = useState("");
    const [previewEmailInput, setPreviewEmailInput] = useState("");
    const [previewError, setPreviewError] = useState("");
    const [previewSuccess, setPreviewSuccess] = useState("");
    const bodyRef = useRef(null);

    useEffect(() => {
        setDraft(draftFromType(type, { slack_dm: slackDmPreference }));
        setRecipientInput("");
        setRecipientError("");
        setBccInput("");
        setBccError("");
        setCcInput("");
        setCcError("");
        setOffsetError("");
        setThresholdError("");
        setFromError("");
        setChannelInput("");
        setChannelError("");
        setPreviewEmailInput("");
        setPreviewError("");
        setPreviewSuccess("");
    }, [type, slackDmPreference]);

    if (!type) return null;

    const isOutbound = type.delivery_kind === "outbound";
    const hasThresholdUnits = !isOutbound && draft.has_threshold_units === true;
    const hasOffsetDays = isOutbound && draft.has_offset_days === true;
    const templateVariables = isOutbound ? (TEMPLATE_VARIABLES[type.code] || []) : [];
    const subjectError = isOutbound && draft.subject
        ? validateTemplateSubject(type.code, draft.subject)
        : null;
    const bodyError = isOutbound && draft.body_html
        ? validateTemplateBody(type.code, draft.body_html)
        : null;
    const hasProvenance = templateVariables.some(({ name }) => name === "provenance");
    const provenanceError = hasProvenance && draft.provenance_note
        ? validateProvenanceNote(type.code, draft.provenance_note)
        : null;
    const previewVariables = Object.fromEntries(
        templateVariables.map(({ name, sample }) => [name, sample])
    );
    if (hasProvenance) {
        previewVariables.provenance = draft.provenance_note.trim() || DEFAULT_PAYMENT_PROVENANCE_NOTE;
    }
    const subjectPreview = draft.subject
        ? renderTemplateText({ body: draft.subject, variables: previewVariables })
        : "";
    const setField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

    const insertVariable = (name) => {
        const textarea = bodyRef.current;
        const current = draft.body_html || "";
        const start = textarea?.selectionStart ?? current.length;
        const end = textarea?.selectionEnd ?? start;
        const insertion = `{{${name}}}`;
        const next = `${current.slice(0, start)}${insertion}${current.slice(end)}`;
        setField("body_html", next);
        window.setTimeout(() => {
            textarea?.focus();
            textarea?.setSelectionRange(start + insertion.length, start + insertion.length);
        }, 0);
    };

    const buildConfig = (content = {}) => {
        const config = {
            from: draft.from.trim() || null,
            bcc: draft.bcc.length ? draft.bcc : null,
            cc: draft.cc.length ? draft.cc : null,
            deliver_in_po_thread: draft.deliver_in_po_thread,
            subject: Object.prototype.hasOwnProperty.call(content, "subject")
                ? content.subject
                : (draft.subject.trim() || null),
            body_html: Object.prototype.hasOwnProperty.call(content, "body_html")
                ? content.body_html
                : (draft.body_html.trim() || null),
            provenance_note: Object.prototype.hasOwnProperty.call(content, "provenance_note")
                ? content.provenance_note
                : (draft.provenance_note.trim() || null),
        };
        if (hasOffsetDays) config.offset_days = Number(draft.offset_days);
        return config;
    };

    const addRecipient = () => {
        const candidate = recipientInput.trim();
        if (!candidate) return;
        if (!isValidEmailAddress(candidate)) {
            setRecipientError("Enter a valid email address.");
            return;
        }
        if (draft.email_recipients.includes(candidate)) {
            setRecipientError("That address is already on the list.");
            return;
        }
        setDraft((prev) => ({
            ...prev,
            email_recipients: [...prev.email_recipients, candidate],
        }));
        setRecipientInput("");
        setRecipientError("");
    };

    const addChannel = () => {
        const candidate = channelInput.trim();
        if (!candidate) return;
        if (draft.slack_channel_ids.includes(candidate)) {
            setChannelError("That channel is already on the list.");
            return;
        }
        setDraft((prev) => ({
            ...prev,
            slack_channel_ids: [...prev.slack_channel_ids, candidate],
        }));
        setChannelInput("");
        setChannelError("");
    };

    const removeChannel = (channel) => setDraft((prev) => ({
        ...prev,
        slack_channel_ids: prev.slack_channel_ids.filter((item) => item !== channel),
    }));

    const removeRecipient = (address) => setDraft((prev) => ({
        ...prev,
        email_recipients: prev.email_recipients.filter((item) => item !== address),
    }));

    const addBcc = () => {
        const candidate = bccInput.trim();
        if (!candidate) return;
        if (!isValidEmailAddress(candidate)) {
            setBccError("Enter a valid email address.");
            return;
        }
        if (draft.bcc.includes(candidate)) {
            setBccError("That address is already on the list.");
            return;
        }
        setDraft((prev) => ({
            ...prev,
            bcc: [...prev.bcc, candidate],
        }));
        setBccInput("");
        setBccError("");
    };

    const removeBcc = (address) => setDraft((prev) => ({
        ...prev,
        bcc: prev.bcc.filter((item) => item !== address),
    }));

    const addCc = () => {
        const candidate = ccInput.trim();
        if (!candidate) return;
        if (!isValidEmailAddress(candidate)) {
            setCcError("Enter a valid email address.");
            return;
        }
        if (draft.cc.some((address) => address.toLowerCase() === candidate.toLowerCase())) {
            setCcError("That address is already on the list.");
            return;
        }
        setDraft((prev) => ({
            ...prev,
            cc: [...prev.cc, candidate],
        }));
        setCcInput("");
        setCcError("");
    };

    const removeCc = (address) => setDraft((prev) => ({
        ...prev,
        cc: prev.cc.filter((item) => item !== address),
    }));

    const submit = (event) => {
        event.preventDefault();
        if (isOutbound) {
            const config = {
                ...buildConfig(),
            };
            if (hasOffsetDays) {
                const offsetDays = Number(draft.offset_days);
                if (
                    draft.offset_days === ""
                    || !Number.isInteger(offsetDays)
                    || offsetDays < 0
                    || offsetDays > 60
                ) {
                    setOffsetError("Enter a whole number from 0 to 60.");
                    return;
                }
                config.offset_days = offsetDays;
            }
            if (!isValidFrom(draft.from)) {
                setFromError("Enter a bare email or Name <email>, or leave it blank.");
                return;
            }
            if (subjectError || bodyError || provenanceError) return;
            if (bccInput.trim()) {
                setBccError("Add or clear the address you typed before saving.");
                return;
            }
            if (ccInput.trim()) {
                setCcError("Add or clear the address you typed before saving.");
                return;
            }
            if (recipientInput.trim()) {
                setRecipientError("Add or clear the address you typed before saving.");
                return;
            }
            onSave({
                status: draft.status,
                in_app_enabled: draft.in_app_enabled,
                slack_channel_ids: draft.slack_channel_ids,
                email_recipients: draft.email_recipients,
                slack_dm: draft.slack_dm,
                config,
            });
            return;
        }
        if (recipientInput.trim()) {
            setRecipientError("Add or clear the address you typed before saving.");
            return;
        }
        const payload = {
            status: draft.status,
            in_app_enabled: draft.in_app_enabled,
            slack_channel_ids: draft.slack_channel_ids,
            email_recipients: draft.email_recipients,
            slack_dm: draft.slack_dm,
        };
        if (hasThresholdUnits) {
            const thresholdUnits = Number(draft.threshold_units);
            if (
                draft.threshold_units === ""
                || !Number.isInteger(thresholdUnits)
                || thresholdUnits < 1
                || thresholdUnits > 100000
            ) {
                setThresholdError("Enter a whole number from 1 to 100000.");
                return;
            }
            payload.config = { threshold_units: thresholdUnits };
        }
        onSave(payload);
    };

    const resetContent = () => {
        onSave({
            status: draft.status,
            in_app_enabled: draft.in_app_enabled,
            slack_channel_ids: draft.slack_channel_ids,
            email_recipients: draft.email_recipients,
            slack_dm: draft.slack_dm,
            config: buildConfig({ subject: null, body_html: null, provenance_note: null }),
        });
    };

    const sendPreview = async () => {
        const email = previewEmailInput.trim();
        if (!isValidEmailAddress(email)) {
            setPreviewError("Enter a valid email address.");
            setPreviewSuccess("");
            return;
        }
        setPreviewError("");
        setPreviewSuccess("");
        try {
            const result = await onSendPreview({
                email,
                subject: draft.subject.trim() || null,
                body_html: draft.body_html,
                provenance_note: draft.provenance_note.trim() || null,
            });
            const samplePo = result?.sample?.po_number || "sample data";
            setPreviewSuccess(`Preview sent to ${email} using sample data from PO ${samplePo}.`);
        } catch (error) {
            const message = typeof error === "string"
                ? error
                : error?.error || error?.message || "Failed to send preview email";
            setPreviewError(message);
        }
    };

    return (
        <div className="fixed inset-0 z-[200]">
            <button
                type="button"
                aria-label="Close notification editor"
                onClick={onClose}
                className="absolute inset-0 bg-black/30"
            />
            <aside
                role="dialog"
                aria-modal="true"
                aria-label={`Edit ${type.name}`}
                className="absolute inset-y-0 right-0 flex w-full max-w-[640px] flex-col border-l border-gray-200 bg-white shadow-xl"
            >
                <header className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">{type.name}</h2>
                        <p className="mt-1 text-[13px] text-gray-500">
                            Choose whether this notification is delivered, and where it goes.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close drawer"
                        className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </header>

                <form onSubmit={submit} className="flex flex-1 flex-col overflow-hidden">
                    <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
                        <div className="rounded-lg border border-border bg-gray-50 p-3">
                            <p className={fieldLabel}>What triggers it</p>
                            <p className="text-sm text-gray-700">{type.description}</p>
                            <p className="mt-2 text-xs text-gray-500">
                                {isOutbound
                                    ? `Name, description and trigger come from the code that emits this notification (${type.code}); email content can be customized below.`
                                    : `Name, description, trigger and message text come from the code that emits this notification (${type.code}).`}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <p className={fieldLabel}>Category</p>
                                <span className="inline-flex rounded-sm bg-gray-100 px-2 py-1 text-[11px] font-medium leading-none text-gray-600">
                                    {notificationTypeCategoryLabel(type.category)}
                                </span>
                            </div>
                            <label className="block">
                                <span className={fieldLabel}>Status</span>
                                <select
                                    value={draft.status}
                                    onChange={(event) => setField("status", event.target.value)}
                                    className={textInput}
                                >
                                    <option value="active">Active</option>
                                    <option value="paused">Paused</option>
                                </select>
                                <span className="mt-1 block text-xs text-gray-500">
                                    {statusHelpText(type)}
                                </span>
                            </label>
                        </div>

                        {hasThresholdUnits && (
                            <div className="rounded-lg border border-border bg-gray-50 p-3">
                                <label className="block">
                                    <span className={fieldLabel}>Unit threshold</span>
                                    <input
                                        type="number"
                                        min="1"
                                        max="100000"
                                        step="1"
                                        value={draft.threshold_units}
                                        onChange={(event) => {
                                            setField("threshold_units", event.target.value);
                                            setThresholdError("");
                                        }}
                                        className={textInput}
                                    />
                                    <span className="mt-1 block text-xs text-gray-500">
                                        Alert when warehouse on-hand is between 1 and this many units (0 on-hand is skipped)
                                    </span>
                                    {thresholdError && <span className="mt-1 block text-xs text-red-700">{thresholdError}</span>}
                                </label>
                            </div>
                        )}

                        <fieldset>
                            <legend className={fieldLabel}>Delivery</legend>
                            {isOutbound && (
                                <p className="mb-3 text-xs text-gray-500">
                                    These only add an internal copy for your team — they never change what the supplier receives.
                                </p>
                            )}
                            <div className="space-y-3">
                                <label className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={draft.slack_dm}
                                        onChange={(event) => setField("slack_dm", event.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-black focus:ring-gray-900"
                                    />
                                    <span>
                                        <span className="block text-sm font-medium text-gray-900">DM me</span>
                                        <span className="mt-0.5 block text-xs text-gray-500">
                                            Send this notification to you as a Slack direct message.
                                        </span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={draft.in_app_enabled}
                                        onChange={(event) => setField("in_app_enabled", event.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-black focus:ring-gray-900"
                                    />
                                    <span>
                                        <span className="block text-sm font-medium text-gray-900">Eadin</span>
                                        <span className="mt-0.5 block text-xs text-gray-500">
                                            Show it in the notification bell.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </fieldset>

                        <div className="rounded-lg border border-border bg-gray-50 p-3">
                            <label className="block">
                                <span className={fieldLabel}>Slack channel IDs</span>
                                <div className="mt-2 flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={channelInput}
                                        onChange={(event) => {
                                            setChannelInput(event.target.value);
                                            setChannelError("");
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                addChannel();
                                            }
                                        }}
                                        placeholder="C0123456789"
                                        className={textInput}
                                    />
                                    <button type="button" onClick={addChannel} className="h-9 shrink-0 rounded-md border border-gray-300 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50">
                                        Add
                                    </button>
                                </div>
                                {channelError && <p className="mt-2 text-xs text-red-700">{channelError}</p>}
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {draft.slack_channel_ids.map((channel) => (
                                        <span key={channel} className="inline-flex items-center gap-1 rounded-sm bg-white px-2 py-1 text-[12px] text-gray-700 ring-1 ring-gray-200">
                                            {channel}
                                            <button type="button" onClick={() => removeChannel(channel)} aria-label={`Remove ${channel}`} className="text-gray-400 hover:text-gray-700">
                                                <XMarkIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                    Use channel IDs and invite the Eadin Slack app. Leave blank to disable shared Slack delivery.
                                </p>
                            </label>
                        </div>

                        <div className="rounded-lg border border-border bg-gray-50 p-3">
                                <span className={fieldLabel}>Email recipients</span>
                                <div className="mt-2 flex items-center gap-2">
                                    <input
                                        type="email"
                                        value={recipientInput}
                                        onChange={(event) => {
                                            setRecipientInput(event.target.value);
                                            setRecipientError("");
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                addRecipient();
                                            }
                                        }}
                                        placeholder="name@kingsloot.com"
                                        className={textInput}
                                    />
                                    <button
                                        type="button"
                                        onClick={addRecipient}
                                        className="h-9 shrink-0 rounded-md border border-gray-300 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        Add
                                    </button>
                                </div>
                                {recipientError && (
                                    <p className="mt-2 text-xs text-red-700">{recipientError}</p>
                                )}
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {draft.email_recipients.map((address) => (
                                        <span
                                            key={address}
                                            className="inline-flex items-center gap-1 rounded-sm bg-white px-2 py-1 text-[12px] text-gray-700 ring-1 ring-gray-200"
                                        >
                                            {address}
                                            <button
                                                type="button"
                                                onClick={() => removeRecipient(address)}
                                                aria-label={`Remove ${address}`}
                                                className="text-gray-400 hover:text-gray-700"
                                            >
                                                <XMarkIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                    These addresses are the shared email destinations for this notification.
                                </p>
                        </div>

                        {isOutbound && (
                            <section className="rounded-lg border border-border bg-gray-50 p-3">
                                <h3 className={fieldLabel}>Supplier email settings</h3>
                                <p className="text-xs text-gray-500">
                                    Configure this supplier-facing email on the purchase order&apos;s thread.
                                </p>
                                <div className="mt-4 space-y-4">
                                    {hasOffsetDays && (
                                        <label className="block">
                                            <span className={fieldLabel}>Days before estimated ship date</span>
                                            <input
                                                type="number"
                                                min="0"
                                                max="60"
                                                step="1"
                                                value={draft.offset_days}
                                                onChange={(event) => {
                                                    setField("offset_days", event.target.value);
                                                    setOffsetError("");
                                                }}
                                                className={textInput}
                                            />
                                            <span className="mt-1 block text-xs text-gray-500">
                                                The first reminder goes out this many days before the estimated ship date.
                                            </span>
                                            {offsetError && <span className="mt-1 block text-xs text-red-700">{offsetError}</span>}
                                        </label>
                                    )}
                                    <label className="block">
                                        <span className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={draft.deliver_in_po_thread}
                                                onChange={(event) => setField("deliver_in_po_thread", event.target.checked)}
                                                className="h-4 w-4 rounded border-gray-300 text-black focus:ring-gray-900"
                                            />
                                            <span className="text-sm font-medium text-gray-900">Deliver inside PO Threads</span>
                                        </span>
                                        <span className="mt-1 block text-xs text-gray-500">
                                            Unchecked, no email goes to the supplier at all. Internal notices above keep firing.
                                        </span>
                                    </label>
                                    <label className="block">
                                        <span className={fieldLabel}>From</span>
                                        <input
                                            type="text"
                                            value={draft.from}
                                            onChange={(event) => {
                                                setField("from", event.target.value);
                                                setFromError("");
                                            }}
                                            placeholder="name@example.com or Name <name@example.com>"
                                            className={textInput}
                                        />
                                        <span className="mt-1 block text-xs text-gray-500">
                                            Blank keeps Eadin&apos;s built-in sender.
                                        </span>
                                        {fromError && <span className="mt-1 block text-xs text-red-700">{fromError}</span>}
                                    </label>
                                    <div>
                                        <span className={fieldLabel}>BCC</span>
                                        <div className="mt-2 flex items-center gap-2">
                                            <input
                                                type="email"
                                                value={bccInput}
                                                onChange={(event) => {
                                                    setBccInput(event.target.value);
                                                    setBccError("");
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        event.preventDefault();
                                                        addBcc();
                                                    }
                                                }}
                                                placeholder="audit@example.com"
                                                className={textInput}
                                            />
                                            <button
                                                type="button"
                                                onClick={addBcc}
                                                className="h-9 shrink-0 rounded-md border border-gray-300 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                                Add
                                            </button>
                                        </div>
                                        {bccError && <p className="mt-2 text-xs text-red-700">{bccError}</p>}
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {draft.bcc.map((address) => (
                                                <span
                                                    key={address}
                                                    className="inline-flex items-center gap-1 rounded-sm bg-white px-2 py-1 text-[12px] text-gray-700 ring-1 ring-gray-200"
                                                >
                                                    {address}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeBcc(address)}
                                                        aria-label={`Remove ${address}`}
                                                        className="text-gray-400 hover:text-gray-700"
                                                    >
                                                        <XMarkIcon className="h-3.5 w-3.5" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500">
                                            Blank keeps Eadin&apos;s built-in BCC.
                                        </p>
                                    </div>
                                    <div>
                                        <span className={fieldLabel}>CC</span>
                                        <div className="mt-2 flex items-center gap-2">
                                            <input
                                                type="email"
                                                value={ccInput}
                                                onChange={(event) => {
                                                    setCcInput(event.target.value);
                                                    setCcError("");
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        event.preventDefault();
                                                        addCc();
                                                    }
                                                }}
                                                placeholder="finance@example.com"
                                                className={textInput}
                                            />
                                            <button
                                                type="button"
                                                onClick={addCc}
                                                className="h-9 shrink-0 rounded-md border border-gray-300 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                                Add
                                            </button>
                                        </div>
                                        {ccError && <p className="mt-2 text-xs text-red-700">{ccError}</p>}
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {draft.cc.map((address) => (
                                                <span
                                                    key={address}
                                                    className="inline-flex items-center gap-1 rounded-sm bg-white px-2 py-1 text-[12px] text-gray-700 ring-1 ring-gray-200"
                                                >
                                                    {address}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCc(address)}
                                                        aria-label={`Remove ${address}`}
                                                        className="text-gray-400 hover:text-gray-700"
                                                    >
                                                        <XMarkIcon className="h-3.5 w-3.5" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500">
                                            Blank sends no configured CC.
                                        </p>
                                    </div>
                                    <div className="border-t border-gray-200 pt-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h4 className={fieldLabel}>Email content</h4>
                                                <p className="text-xs text-gray-500">
                                                    Leave blank to use the current built-in email.
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={resetContent}
                                                disabled={saving}
                                                className="text-xs font-medium text-gray-600 underline hover:text-gray-900 disabled:opacity-50"
                                            >
                                                Reset to default
                                            </button>
                                        </div>
                                        <label className="mt-4 block">
                                            <span className={fieldLabel}>Subject</span>
                                            <input
                                                type="text"
                                                value={draft.subject}
                                                onChange={(event) => setField("subject", event.target.value)}
                                                placeholder="Blank keeps the current subject"
                                                className={textInput}
                                            />
                                            {subjectError && <span className="mt-1 block text-xs text-red-700">{subjectError}</span>}
                                        </label>
                                        {hasProvenance && (
                                            <label className="mt-4 block">
                                                <span className={fieldLabel}>Provenance note</span>
                                                <textarea
                                                    value={draft.provenance_note}
                                                    onChange={(event) => setField("provenance_note", event.target.value)}
                                                    rows={3}
                                                    placeholder="Blank uses the default provenance note"
                                                    className={`${textInput} h-auto resize-y`}
                                                />
                                                <span className="mt-1 block text-xs text-gray-500">
                                                    Used in the payment confirmation&apos;s {"{{provenance}}"} line and in the internal note saved on the payment record.
                                                </span>
                                                {provenanceError && (
                                                    <span className="mt-1 block text-xs text-red-700">{provenanceError}</span>
                                                )}
                                            </label>
                                        )}
                                        <label className="mt-4 block">
                                            <span className={fieldLabel}>Body HTML</span>
                                            <textarea
                                                ref={bodyRef}
                                                value={draft.body_html}
                                                onChange={(event) => setField("body_html", event.target.value)}
                                                rows={8}
                                                placeholder="Blank keeps the current email body"
                                                className={`${textInput} h-auto min-h-[180px] resize-y font-mono text-xs`}
                                            />
                                            {bodyError && <span className="mt-1 block text-xs text-red-700">{bodyError}</span>}
                                        </label>
                                        {templateVariables.length > 0 && (
                                            <div className="mt-3">
                                                <p className={fieldLabel}>Insert variable</p>
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {templateVariables.map(({ name, label }) => (
                                                        <button
                                                            key={name}
                                                            type="button"
                                                            onClick={() => insertVariable(name)}
                                                            className="rounded-full bg-white px-2 py-1 text-xs text-gray-700 ring-1 ring-gray-200 hover:bg-gray-100"
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <div className="mt-4">
                                            {subjectPreview && (
                                                <div className="mb-3">
                                                    <p className={fieldLabel}>Subject preview</p>
                                                    <p className="mt-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                                        {subjectPreview}
                                                    </p>
                                                </div>
                                            )}
                                            <p className={fieldLabel}>Preview</p>
                                            <div className="mt-2 max-h-64 overflow-auto rounded-md border border-gray-200 bg-white p-3 text-sm">
                                                {draft.body_html
                                                    ? <div dangerouslySetInnerHTML={{ __html: renderNotificationTemplate({ body: draft.body_html, variables: previewVariables }) }} />
                                                    : <p className="text-xs text-gray-500">The current built-in email will be used.</p>}
                                            </div>
                                            {PREVIEWABLE_TEMPLATE_CODES.includes(type.code) && (
                                                <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                                                    <label className="block">
                                                        <span className={fieldLabel}>Send preview to</span>
                                                        <div className="mt-2 flex items-center gap-2">
                                                            <input
                                                                type="email"
                                                                value={previewEmailInput}
                                                                onChange={(event) => {
                                                                    setPreviewEmailInput(event.target.value);
                                                                    setPreviewError("");
                                                                    setPreviewSuccess("");
                                                                }}
                                                                placeholder="you@kingsloot.com"
                                                                className={textInput}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={sendPreview}
                                                                disabled={sendingPreview || !draft.body_html.trim()}
                                                                className="h-9 shrink-0 rounded-md border border-black bg-black px-3 text-[13px] font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                                                            >
                                                                {sendingPreview ? "Sending..." : "Send Preview"}
                                                            </button>
                                                        </div>
                                                    </label>
                                                    <p className="mt-2 text-xs text-gray-500">
                                                        Sends the body above, rendered with real sample data, to this address only — the supplier is never contacted.
                                                    </p>
                                                    {previewError && <p className="mt-2 text-xs text-red-700">{previewError}</p>}
                                                    {previewSuccess && <p className="mt-2 text-xs text-emerald-700">{previewSuccess}</p>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}
                    </div>

                    <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-9 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className={`h-9 rounded-md border border-black bg-black px-3 text-[13px] font-medium text-white ${saving ? "opacity-50" : "hover:bg-gray-800"}`}
                        >
                            {saving ? "Saving..." : "Save changes"}
                        </button>
                    </footer>
                </form>
            </aside>
        </div>
    );
};

export default NotificationTypeDrawer;
